import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";
import { formInput, formSelect, formTextarea, formLabel } from "@/lib/form-styles";
import { compactPhone, showPhoneValidationError, validatePhoneWithCountryCode } from "@/lib/phoneValidation";
import { HearAboutUsField } from "@/components/HearAboutUsField";
import { INDIA_ROUTES, MAIN_SITE } from "@/lib/mainSiteLinks";

const COUNTRIES = [
  "India",
  "United Arab Emirates",
  "Saudi Arabia",
  "Qatar",
  "Kuwait",
  "Oman",
  "Bahrain",
  "United Kingdom",
  "United States",
  "Singapore",
  "Hong Kong",
  "Other",
];

const CONTACT_FIELD =
  "w-full min-h-[44px] rounded-xl border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-ink transition-colors placeholder:text-ink/50 focus:border-brand-500 focus:outline-none";
const CONTACT_LABEL = "mb-2 block text-xs font-semibold text-ink";

export function InquiryForm({ onSuccess, layout = "default" }) {
  const isContact = layout === "contact";
  const inputClass = isContact ? CONTACT_FIELD : formInput;
  const selectClass = isContact ? CONTACT_FIELD : formSelect;
  const textareaClass = isContact ? `${CONTACT_FIELD} resize-none` : formTextarea;
  const labelClass = isContact ? CONTACT_LABEL : formLabel;
  const formGap = isContact ? "space-y-5" : "space-y-4";
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "India",
    student_age: "",
    message: "",
    how_did_you_hear: "",
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    const phoneCheck = validatePhoneWithCountryCode(form.phone, { required: true });
    if (!phoneCheck.ok) {
      showPhoneValidationError(phoneCheck.message, toast);
      return;
    }
    if (!form.how_did_you_hear) {
      toast.error("Please tell us how you heard about us.");
      return;
    }
    if (!acceptTerms) {
      toast.error("Please accept the terms and conditions.");
      return;
    }

    setLoading(true);
    try {
      await submitContact({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: compactPhone(phoneCheck.value),
        message: form.message.trim(),
        how_did_you_hear: form.how_did_you_hear,
        accept_terms: true,
        form_url: window.location.href,
        program: form.student_age ? `India enquiry (student age ${form.student_age})` : "India Website Enquiry",
        country: form.country || "India",
      });
      toast.success("Thank you. We'll be in touch within one business day.");
      setForm({
        name: "",
        email: "",
        phone: "",
        country: "India",
        student_age: "",
        message: "",
        how_did_you_hear: "",
      });
      setAcceptTerms(false);
      onSuccess?.();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.response?.data?.error || "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="inquiry-form" className={formGap}>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="inq-name" className={labelClass}>
            Parent / Guardian Full Name *
          </label>
          <input
            id="inq-name"
            data-testid="inq-name"
            required
            placeholder="e.g. Priya Sharma"
            className={inputClass}
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="inq-email" className={labelClass}>
            Email Address *
          </label>
          <input
            id="inq-email"
            data-testid="inq-email"
            type="email"
            required
            placeholder="you@example.com"
            className={inputClass}
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
          />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="inq-phone" className={labelClass}>
            Phone Number (with country code) *
          </label>
          <input
            id="inq-phone"
            data-testid="inq-phone"
            type="tel"
            inputMode="tel"
            required
            placeholder="+91 88742 00200"
            className={inputClass}
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="inq-country" className={labelClass}>
            Country of Residence *
          </label>
          <select
            id="inq-country"
            data-testid="inq-country"
            required
            className={selectClass}
            value={form.country}
            onChange={(e) => set("country", e.target.value)}
          >
            <option value="">Select country...</option>
            {COUNTRIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label htmlFor="inq-age" className={labelClass}>
          Age
        </label>
        <input
          id="inq-age"
          data-testid="inq-age"
          type="text"
          inputMode="numeric"
          placeholder="e.g. 15"
          className={inputClass}
          value={form.student_age}
          onChange={(e) => set("student_age", e.target.value)}
        />
      </div>
      <div>
        <label htmlFor="inq-message" className={labelClass}>
          Your message
        </label>
        <textarea
          id="inq-message"
          data-testid="inq-message"
          rows={isContact ? 4 : 3}
          placeholder="Tell us about career interests, programmes you are considering, or any questions..."
          className={textareaClass}
          value={form.message}
          onChange={(e) => set("message", e.target.value)}
        />
      </div>
      <HearAboutUsField
        testId="inq-hear-about"
        required
        value={form.how_did_you_hear}
        onChange={(e) => set("how_did_you_hear", e.target.value)}
        className={selectClass}
        labelClassName={labelClass}
      />
      <label data-testid="inq-terms" className="flex items-start gap-3 text-xs text-ink/70 cursor-pointer select-none">
        <input
          type="checkbox"
          className="w-4 h-4 accent-brand-500 rounded mt-0.5"
          checked={acceptTerms}
          onChange={(e) => setAcceptTerms(e.target.checked)}
          required
        />
        <span>
          I accept the{" "}
          <a href={MAIN_SITE.terms} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
            Terms &amp; Conditions
          </a>{" "}
          and{" "}
          <Link to={INDIA_ROUTES.privacy} className="text-brand-600 hover:underline">
            Privacy Policy
          </Link>{" "}
          of alearninglab.com
        </span>
      </label>
      <div className={isContact ? "pt-1" : "pt-2"}>
        <button
          type="submit"
          data-testid="inq-submit"
          disabled={loading}
          className={
            isContact
              ? "w-full rounded-xl bg-sun-500 py-3.5 text-sm font-bold text-ink transition-colors hover:bg-sun-400 disabled:opacity-60"
              : "w-full rounded-xl bg-sun-500 py-3.5 text-xs font-bold text-ink shadow-md transition-all hover:bg-sun-400 disabled:opacity-60"
          }
        >
          {loading ? "Sending…" : "Send message"}
        </button>
        <p className={`mt-2 text-center text-ink/60 ${isContact ? "text-xs" : "text-[10px]"}`}>
          We respond within one business day. Your details are confidential.
        </p>
      </div>
    </form>
  );
}
